import React, {useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
import {useApi} from '../hooks/useApi';

export interface FitmentAttributeConfig {
    taxonomy: string;
    label: string;
}

export interface FitmentRuntimeConfig {
    enabled?: boolean;
    selector_title?: string;
    display_mode?: 'inline' | 'page' | string;
    attributes?: FitmentAttributeConfig[];
    can_save?: boolean;
    options_endpoint?: string;
    saved_fitments_endpoint?: string;
}

interface FitmentOption {
    id: number;
    name: string;
    slug?: string;
}

interface FitmentOptionsAttribute {
    taxonomy: string;
    label: string;
    selected?: number;
    options: FitmentOption[];
}

interface FitmentOptionsResponse {
    attributes: FitmentOptionsAttribute[];
    selection?: Record<string, number>;
}

interface SavedFitmentItem {
    id: string;
    name: string;
    attributes: Record<string, number>;
    labels?: Record<string, string>;
    created_at?: string;
}

interface SavedFitmentsResponse {
    items: SavedFitmentItem[];
    can_save?: boolean;
}

interface FitmentSelectorProps {
    config: FitmentRuntimeConfig | undefined;
    selection: Record<string, number>;
    disabled?: boolean;
    onSelectionChange: (selection: Record<string, number>, applySearch: boolean) => void;
}

const normalizeEndpoint = (value: unknown, fallback: string): string => {
    const endpoint = String(value || '').trim();
    if (endpoint === '') {
        return fallback;
    }

    return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
};

const normalizeSelection = (
    value: Record<string, number>,
    attributes: FitmentAttributeConfig[]
): Record<string, number> => {
    const configuredTaxonomies = new Set(attributes.map((attribute) => String(attribute.taxonomy || '').trim()).filter(Boolean));
    const normalized: Record<string, number> = {};

    Object.entries(value || {}).forEach(([taxonomy, termId]) => {
        const safeTaxonomy = String(taxonomy || '').trim();
        const parsedTermId = Number(termId || 0);
        if (safeTaxonomy !== '' && configuredTaxonomies.has(safeTaxonomy) && Number.isFinite(parsedTermId) && parsedTermId > 0) {
            normalized[safeTaxonomy] = Math.floor(parsedTermId);
        }
    });

    return normalized;
};

const isCompleteSelection = (
    selection: Record<string, number>,
    attributes: FitmentAttributeConfig[]
): boolean => {
    return attributes.length > 0 && attributes.every((attribute) => Number(selection[attribute.taxonomy] || 0) > 0);
};

const buildSelectionSummary = (
    selection: Record<string, number>,
    optionAttributes: FitmentOptionsAttribute[],
    configuredAttributes: FitmentAttributeConfig[]
): string => {
    const optionLookup = new Map<string, Map<number, string>>();
    optionAttributes.forEach((attribute) => {
        const termLookup = new Map<number, string>();
        (attribute.options || []).forEach((option) => {
            const termId = Number(option.id || 0);
            if (termId > 0) {
                termLookup.set(termId, String(option.name || '').trim());
            }
        });
        optionLookup.set(attribute.taxonomy, termLookup);
    });

    const parts = configuredAttributes
        .map((attribute) => {
            const termId = Number(selection[attribute.taxonomy] || 0);
            return optionLookup.get(attribute.taxonomy)?.get(termId) || '';
        })
        .filter((part) => part !== '');

    return parts.join(' ');
};

const FitmentSelector: React.FC<FitmentSelectorProps> = ({
    config,
    selection,
    disabled = false,
    onSelectionChange,
}) => {
    const {get, post, del} = useApi();
    const attributes = useMemo(() => (
        Array.isArray(config?.attributes)
            ? config.attributes.filter((attribute) => (
                attribute
                && String(attribute.taxonomy || '').trim() !== ''
                && String(attribute.label || '').trim() !== ''
            ))
            : []
    ), [config?.attributes]);
    const normalizedSelection = useMemo(() => normalizeSelection(selection, attributes), [attributes, selection]);
    const optionsEndpoint = normalizeEndpoint(config?.options_endpoint, '/fitment/options');
    const savedEndpoint = normalizeEndpoint(config?.saved_fitments_endpoint, '/fitment/saved');
    const canSave = config?.can_save === true;
    const selectorTitle = String(config?.selector_title || 'Select fitment').trim() || 'Select fitment';

    const [optionAttributes, setOptionAttributes] = useState<FitmentOptionsAttribute[]>([]);
    const [savedItems, setSavedItems] = useState<SavedFitmentItem[]>([]);
    const [selectedSavedId, setSelectedSavedId] = useState('');
    const [saveName, setSaveName] = useState('');
    const [isSaveOpen, setIsSaveOpen] = useState(false);
    const [isOptionsLoading, setIsOptionsLoading] = useState(false);
    const [isSavedLoading, setIsSavedLoading] = useState(false);

    useEffect(() => {
        if (!config?.enabled || attributes.length === 0) {
            setOptionAttributes([]);
            return;
        }

        const params = new URLSearchParams();
        params.set('selection', JSON.stringify(normalizedSelection));
        setIsOptionsLoading(true);
        get<FitmentOptionsResponse>(`${optionsEndpoint}?${params.toString()}`)
            .then((payload) => {
                setOptionAttributes(Array.isArray(payload.attributes) ? payload.attributes : []);
            })
            .catch((error) => {
                console.error('Failed to load fitment options:', error);
                toast.error('Unable to load fitment options.');
            })
            .finally(() => setIsOptionsLoading(false));
    }, [attributes, config?.enabled, get, normalizedSelection, optionsEndpoint]);

    useEffect(() => {
        if (!config?.enabled || !canSave) {
            setSavedItems([]);
            return;
        }

        setIsSavedLoading(true);
        get<SavedFitmentsResponse>(savedEndpoint)
            .then((payload) => {
                setSavedItems(Array.isArray(payload.items) ? payload.items : []);
            })
            .catch((error) => {
                console.error('Failed to load saved fitments:', error);
            })
            .finally(() => setIsSavedLoading(false));
    }, [canSave, config?.enabled, get, savedEndpoint]);

    if (!config?.enabled || attributes.length === 0) {
        return null;
    }

    const isComplete = isCompleteSelection(normalizedSelection, attributes);
    const currentSummary = buildSelectionSummary(normalizedSelection, optionAttributes, attributes);

    const onChangeAttribute = (taxonomy: string, value: string) => {
        const selectedIndex = attributes.findIndex((attribute) => attribute.taxonomy === taxonomy);
        const termId = Number(value || 0);
        const nextSelection: Record<string, number> = {};

        attributes.forEach((attribute, index) => {
            if (index < selectedIndex && Number(normalizedSelection[attribute.taxonomy] || 0) > 0) {
                nextSelection[attribute.taxonomy] = Number(normalizedSelection[attribute.taxonomy]);
            }
        });

        if (selectedIndex >= 0 && Number.isFinite(termId) && termId > 0) {
            nextSelection[taxonomy] = Math.floor(termId);
        }

        onSelectionChange(nextSelection, true);
        setSelectedSavedId('');
        setIsSaveOpen(false);
    };

    const onApplySavedFitment = (itemId: string) => {
        setSelectedSavedId(itemId);
        const item = savedItems.find((candidate) => candidate.id === itemId);
        if (!item) {
            return;
        }

        onSelectionChange(normalizeSelection(item.attributes || {}, attributes), true);
        setSaveName('');
        setIsSaveOpen(false);
    };

    const onClearFitment = () => {
        setSelectedSavedId('');
        setSaveName('');
        setIsSaveOpen(false);
        onSelectionChange({}, true);
    };

    const onSaveFitment = () => {
        if (!canSave || !isComplete) {
            return;
        }

        setIsSavedLoading(true);
        post<SavedFitmentsResponse>(savedEndpoint, {
            name: saveName,
            attributes: normalizedSelection,
        })
            .then((payload) => {
                setSavedItems(Array.isArray(payload.items) ? payload.items : []);
                setSaveName('');
                setIsSaveOpen(false);
                toast.success('Fitment saved.');
            })
            .catch((error) => {
                console.error('Failed to save fitment:', error);
                toast.error('Unable to save fitment.');
            })
            .finally(() => setIsSavedLoading(false));
    };

    const onDeleteSavedFitment = () => {
        if (!selectedSavedId) {
            return;
        }

        setIsSavedLoading(true);
        del<SavedFitmentsResponse>(`${savedEndpoint}/${encodeURIComponent(selectedSavedId)}`, {})
            .then((payload) => {
                setSavedItems(Array.isArray(payload.items) ? payload.items : []);
                setSelectedSavedId('');
                toast.success('Saved fitment removed.');
            })
            .catch((error) => {
                console.error('Failed to remove saved fitment:', error);
                toast.error('Unable to remove saved fitment.');
            })
            .finally(() => setIsSavedLoading(false));
    };

    return (
        <div className="fitment-selector fitment-selector--compact">
            <div className="fitment-selector__header">
                <div className="fitment-selector__title-wrap">
                    <h2 className="fitment-selector__title">{selectorTitle}</h2>
                    {currentSummary !== '' && (
                        <div className="fitment-selector__summary">{currentSummary}</div>
                    )}
                </div>
                {canSave && savedItems.length > 0 && (
                    <div className="fitment-selector__saved">
                        <select
                            className="form-select form-select-sm"
                            value={selectedSavedId}
                            onChange={(event) => onApplySavedFitment(event.target.value)}
                            disabled={disabled || isSavedLoading}
                            aria-label="Saved fitments"
                        >
                            <option value="">Saved fitments</option>
                            {savedItems.map((item) => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={onDeleteSavedFitment}
                            disabled={disabled || isSavedLoading || selectedSavedId === ''}
                        >
                            Remove
                        </button>
                    </div>
                )}
            </div>

            <div className="fitment-selector__body">
                <div className="fitment-selector__fields">
                    {attributes.map((attribute, index) => {
                        const previousAttributesSelected = attributes
                            .slice(0, index)
                            .every((previousAttribute) => Number(normalizedSelection[previousAttribute.taxonomy] || 0) > 0);
                        const optionAttribute = optionAttributes.find((candidate) => candidate.taxonomy === attribute.taxonomy);
                        const options = optionAttribute?.options || [];
                        const value = Number(normalizedSelection[attribute.taxonomy] || 0);

                        return (
                            <label key={attribute.taxonomy} className="fitment-selector__field">
                                <span>{attribute.label}</span>
                                <select
                                    className="form-select form-select-sm"
                                    value={value > 0 ? String(value) : ''}
                                    onChange={(event) => onChangeAttribute(attribute.taxonomy, event.target.value)}
                                    disabled={disabled || isOptionsLoading || !previousAttributesSelected}
                                >
                                    <option value="">Select {attribute.label.toLowerCase()}</option>
                                    {options.map((option) => (
                                        <option key={`${attribute.taxonomy}-${option.id}`} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        );
                    })}
                </div>

                <div className="fitment-selector__tools">
                    {canSave && (
                        <button
                            type="button"
                            className="btn btn-link btn-sm text-decoration-none fitment-selector__save-toggle"
                            onClick={() => setIsSaveOpen((open) => !open)}
                            disabled={disabled || !isComplete}
                        >
                            Save fitment
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-link btn-sm text-decoration-none fitment-selector__clear"
                        onClick={onClearFitment}
                        disabled={disabled || Object.keys(normalizedSelection).length === 0}
                    >
                        Clear
                    </button>
                </div>

                {canSave && isSaveOpen && (
                    <div className="fitment-selector__save">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Saved name"
                            value={saveName}
                            onChange={(event) => setSaveName(event.target.value)}
                            disabled={disabled || isSavedLoading || !isComplete}
                        />
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={onSaveFitment}
                            disabled={disabled || isSavedLoading || !isComplete}
                        >
                            Save
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FitmentSelector;
